/**
 * Layout for the docs topology diagram (components/DocTopology.tsx). Pure: the same
 * groups, nodes and edges always give the same picture, so the page stays static and the
 * layout can be checked without a browser (scripts/check-doc-topology.mjs).
 *
 * The model is a left-to-right flow:
 *  - a node's COLUMN is the longest chain of edges that leads to it;
 *  - a GROUP (a cloud, a cluster) is a horizontal band, one under the other in the order
 *    the groups are declared, and its box is drawn around its nodes;
 *  - a column that holds no grouped node spreads its nodes over the full height.
 *
 * Horizontal positions are in column units (one column = 100 units) and are drawn as
 * percentages, so the diagram fills whatever width the page gives it. Vertical positions
 * are pixels, because a row is as tall as two lines of text whatever the width.
 */

export interface DocTopoGroup {
  id: string;
  label: string;
  /** One short line next to the label (what sets this group apart). */
  note?: string;
}

export interface DocTopoNode {
  id: string;
  label: string;
  /** One short line under the label (a size, a role). */
  note?: string;
  /** `store` = something that keeps data (drawn with a cylinder mark). Default `service`. */
  kind?: 'service' | 'store';
  /** The group this node sits in. */
  group?: string;
}

export interface DocTopoEdge {
  /** A node id, or a group id when the whole group is the endpoint. */
  from: string;
  to: string;
  /** A dependency rather than a request path (drawn dashed and muted). */
  dashed?: boolean;
}

export const COL_UNITS = 100;
/**
 * How much of its column a node leaves free on each side; two neighbours are 2x apart.
 * A diagram with few columns has wide columns, so its nodes take a smaller share: a node
 * stays about as wide as its text, and the room goes to the lines and their labels.
 */
export function nodeInset(cols: number): number {
  return Math.min(25, Math.max(10, 40 - 5 * cols));
}
/** A group box sits this far outside its nodes. */
const GROUP_MARGIN = 5;
export function groupInset(cols: number): number {
  return nodeInset(cols) - GROUP_MARGIN;
}
/** Lines that share one side of a node or a box end this far apart, not on one point. */
const PORT_STEP = 14;
export const NODE_H = 64;
const ROW_GAP = 12;
const PAD = 16;
/** Room at the top of a group box for its label. */
const GROUP_HEAD = 32;
const GROUP_FOOT = 12;
const BAND_GAP = 16;

export type DocTopoEdgeMode = 'forward' | 'backward' | 'down' | 'up';

export interface DocTopoLayout {
  cols: number;
  height: number;
  nodes: { id: string; col: number; top: number }[];
  groups: { id: string; colFrom: number; colTo: number; top: number; height: number }[];
  /** Endpoints in (column units, px). `mode` says which way the arrowhead points. */
  edges: { from: string; to: string; x1: number; y1: number; x2: number; y2: number; mode: DocTopoEdgeMode }[];
}

export function layoutTopology(
  caption: string,
  groups: DocTopoGroup[],
  nodes: DocTopoNode[],
  edges: DocTopoEdge[],
): DocTopoLayout {
  const fail = (msg: string): never => {
    throw new Error(`DocTopology "${caption}": ${msg}`);
  };

  const nodeIds = new Set<string>();
  for (const n of nodes) {
    if (nodeIds.has(n.id)) fail(`node id "${n.id}" is declared twice`);
    nodeIds.add(n.id);
  }
  const groupIds = new Set(groups.map((g) => g.id));
  for (const g of groups) if (nodeIds.has(g.id)) fail(`"${g.id}" is both a node and a group`);
  for (const n of nodes) if (n.group && !groupIds.has(n.group)) fail(`node "${n.id}" names unknown group "${n.group}"`);
  const members = (id: string): string[] =>
    nodeIds.has(id)
      ? [id]
      : groupIds.has(id)
        ? nodes.filter((n) => n.group === id).map((n) => n.id)
        : fail(`an edge names unknown node or group "${id}"`);
  for (const g of groups) if (members(g.id).length === 0) fail(`group "${g.id}" has no node`);

  // Column = longest chain of edges into the node. A group endpoint stands for all its
  // nodes: as a source it counts as its last column, as a target it moves as one block.
  const col = new Map(nodes.map((n) => [n.id, 0]));
  const at = (id: string) => col.get(id)!;
  let moved = true;
  for (let pass = 0; moved; pass += 1) {
    if (pass > nodes.length + 1) fail('the edges form a loop; a topology must flow one way');
    moved = false;
    for (const e of edges) {
      const need = Math.max(...members(e.from).map(at)) + 1;
      const to = members(e.to);
      const shift = need - Math.min(...to.map(at));
      if (shift > 0) {
        for (const id of to) col.set(id, at(id) + shift);
        moved = true;
      }
    }
  }
  const cols = Math.max(...nodes.map((n) => at(n.id))) + 1;

  // Bands: one per group, top to bottom, then one for loose nodes that share a column
  // with grouped ones. A band is as tall as its fullest column.
  const inColumn = (c: number, pick: (n: DocTopoNode) => boolean) => nodes.filter((n) => at(n.id) === c && pick(n));
  const columns = Array.from({ length: cols }, (_, c) => c);
  const groupedCols = new Set(nodes.filter((n) => n.group).map((n) => at(n.id)));
  const rowsOf = (pick: (n: DocTopoNode) => boolean) => Math.max(0, ...columns.map((c) => inColumn(c, pick).length));
  const stack = (rows: number) => rows * NODE_H + (rows - 1) * ROW_GAP;

  const isLoose = (n: DocTopoNode) => !n.group && groupedCols.has(at(n.id));
  const looseRows = rowsOf(isLoose);
  let bandsHeight = groups.reduce((h, g) => h + GROUP_HEAD + stack(rowsOf((n) => n.group === g.id)) + GROUP_FOOT, 0);
  bandsHeight += BAND_GAP * Math.max(0, groups.length - 1);
  if (looseRows) bandsHeight += (groups.length ? BAND_GAP : 0) + stack(looseRows);

  const freeRows = Math.max(0, ...columns.filter((c) => !groupedCols.has(c)).map((c) => inColumn(c, () => true).length));
  const inner = Math.max(bandsHeight, stack(freeRows));
  const height = inner + 2 * PAD;

  const top = new Map<string, number>();
  const place = (list: DocTopoNode[], bandTop: number, bandRows: number) => {
    const offset = (stack(bandRows) - stack(list.length)) / 2;
    list.forEach((n, i) => top.set(n.id, bandTop + offset + i * (NODE_H + ROW_GAP)));
  };

  let y = PAD + (inner - bandsHeight) / 2;
  const boxes: DocTopoLayout['groups'] = [];
  for (const g of groups) {
    const mine = (n: DocTopoNode) => n.group === g.id;
    const rows = rowsOf(mine);
    const boxHeight = GROUP_HEAD + stack(rows) + GROUP_FOOT;
    const own = nodes.filter(mine).map((n) => at(n.id));
    boxes.push({ id: g.id, colFrom: Math.min(...own), colTo: Math.max(...own), top: y, height: boxHeight });
    for (const c of columns) place(inColumn(c, mine), y + GROUP_HEAD, rows);
    y += boxHeight + BAND_GAP;
  }
  if (looseRows) for (const c of columns) place(inColumn(c, isLoose), y, looseRows);
  for (const c of columns) {
    if (groupedCols.has(c)) continue;
    // Spread over the full height, so two loose nodes line up with two bands.
    const list = inColumn(c, () => true);
    list.forEach((n, i) => top.set(n.id, PAD + (inner * (i + 0.5)) / list.length - NODE_H / 2));
  }

  for (const b of boxes) {
    const inside = nodes.find((n) => n.group !== b.id && at(n.id) >= b.colFrom && at(n.id) <= b.colTo && overlaps(top.get(n.id)!, NODE_H, b.top, b.height));
    if (inside) fail(`node "${inside.id}" lands inside the box of group "${b.id}"; give it an edge that moves it to another column`);
  }

  // Anchors: the middle of a side of a node, or of a group box.
  const nodeIn = nodeInset(cols);
  const groupIn = groupInset(cols);
  const span = (id: string) => {
    if (nodeIds.has(id)) {
      const c = at(id);
      const t = top.get(id)!;
      return { left: c * COL_UNITS + nodeIn, right: (c + 1) * COL_UNITS - nodeIn, top: t, bottom: t + NODE_H, colFrom: c, colTo: c };
    }
    const b = boxes.find((x) => x.id === id)!;
    return {
      left: b.colFrom * COL_UNITS + groupIn,
      right: (b.colTo + 1) * COL_UNITS - groupIn,
      top: b.top,
      bottom: b.top + b.height,
      colFrom: b.colFrom,
      colTo: b.colTo,
    };
  };
  const laid = edges.map((e) => {
    const a = span(e.from);
    const b = span(e.to);
    const midY = (s: { top: number; bottom: number }) => (s.top + s.bottom) / 2;
    const midX = (s: { left: number; right: number }) => (s.left + s.right) / 2;
    if (b.colFrom > a.colTo) return { ...ends(e), x1: a.right, y1: midY(a), x2: b.left, y2: midY(b), mode: 'forward' as const };
    if (b.colTo < a.colFrom) return { ...ends(e), x1: a.left, y1: midY(a), x2: b.right, y2: midY(b), mode: 'backward' as const };
    if (b.top >= a.bottom) return { ...ends(e), x1: midX(a), y1: a.bottom, x2: midX(b), y2: b.top, mode: 'down' as const };
    if (b.bottom <= a.top) return { ...ends(e), x1: midX(a), y1: a.top, x2: midX(b), y2: b.bottom, mode: 'up' as const };
    return fail(`edge "${e.from}" to "${e.to}" joins two things that overlap`);
  });

  // Ports: lines that leave or reach the same side of the same thing are spread along
  // that side, in the order of where their other ends are, so they neither pile up on one
  // point nor cross each other on the way out.
  const spread = (end: 'from' | 'to') => {
    const yKey = end === 'from' ? 'y1' : 'y2';
    const farKey = end === 'from' ? 'y2' : 'y1';
    const sides = new Map<string, typeof laid>();
    for (const e of laid) {
      if (e.mode !== 'forward' && e.mode !== 'backward') continue;
      const key = `${e[end]}|${e.mode}`;
      sides.set(key, [...(sides.get(key) ?? []), e]);
    }
    for (const list of sides.values()) {
      if (list.length < 2) continue;
      const s = span(list[0]![end]);
      const step = Math.min(PORT_STEP, (s.bottom - s.top) / (list.length + 1));
      list
        .slice()
        .sort((a, b) => a[farKey] - b[farKey])
        .forEach((e, i) => {
          e[yKey] = (s.top + s.bottom) / 2 + (i - (list.length - 1) / 2) * step;
        });
    }
  };
  spread('from');
  spread('to');

  return {
    cols,
    height,
    nodes: nodes.map((n) => ({ id: n.id, col: at(n.id), top: top.get(n.id)! })),
    groups: boxes,
    edges: laid,
  };
}

function ends(e: DocTopoEdge) {
  return { from: e.from, to: e.to };
}

function overlaps(aTop: number, aHeight: number, bTop: number, bHeight: number) {
  return aTop < bTop + bHeight && bTop < aTop + aHeight;
}
